import { Util } from "@xt/xtbase";
import { isFiatMarketCode } from "@/utils/fiatMarket";
import { trimTrailingZeros } from "./convertKlineShared";

const { moment } = Util;

/** Spot 兑换页（原 /convert/buy，现挂在交易页 Spot Tab 下） */
export const CFD_SPOT_CONVERT_PATH = "/futures/contract/spot";

export function getCfdSpotConvertPath(query = {}) {
  return getConvertRouterPath(CFD_SPOT_CONVERT_PATH, query);
}

/** Spot 列表行 → 闪兑页：用计价币买入 base（与 ConvertBuy from/to 一致） */
export function getSpotConvertPathForDisplaySymbol(symbol) {
  const parts = String(symbol || "").toLowerCase().split("_");
  const base = (parts[0] || "").toUpperCase();
  const quote = (parts[1] || "").toUpperCase();
  if (!base || !quote) return CFD_SPOT_CONVERT_PATH;
  return getCfdSpotConvertPath({ from: quote, to: base });
}

/** Next.js router 专用 path（勿用 getUrl：带 origin 会导致 history 栈异常） */
export function getConvertRouterPath(path, query = {}) {
  const base = String(path || "").startsWith("/") ? path : `/${path || ""}`;
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== "") {
      params.set(key, String(value).trim());
    }
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function formatConvertResultAmount(str, currency) {
  if (str == null || str === "") return "";
  const s = String(str).replace(/,/g, "").trim();
  if (!s || !/^-?\d+(\.\d+)?$/.test(s)) return "";
  const isFiat = currency && isFiatMarketCode(currency);
  if (isFiat) {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return "";
    return trimTrailingZeros(n.toFixed(2)) || "0";
  }
  return trimTrailingZeros(s) || "0";
}

export function formatConvertResultRateLine(rateStr, spendCurrency, receiveCurrency, orderType) {
  const rate = parseFloat(String(rateStr || 0)) || 0;
  if (!rate) return "";
  const spend = (spendCurrency || "").toUpperCase();
  const receive = (receiveCurrency || "").toUpperCase();
  const type = Number(orderType);
  const isFiatPay = type === 1 || type === 2 || isFiatMarketCode(spend);
  if (isFiatPay) {
    const direct = (1 / rate).toFixed(8).replace(/\.?0+$/, "") || "0";
    return `1 ${spend} = ${direct} ${receive}`;
  }
  const direct = formatConvertResultAmount(rateStr, receiveCurrency);
  return `1 ${spend} = ${direct} ${receive}`;
}

export function formatConvertTradeDate(value) {
  if (!value) return "—";
  const m = moment(value);
  if (!m.isValid()) return "—";
  return m.format("YYYY-MM-DD HH:mm");
}

export function buildConvertResultDetailRows(t, data, options = {}) {
  const {
    spendAmount,
    spendCurrency,
    receiveCurrency,
    exchangeRate,
    totalFee,
    feeCurrencyCode,
    orderType,
    tradeDate,
  } = data;

  const { includeTradeDate = true, typeValueKey = "convert.orderTypeMarket" } = options;

  const spendCode = (spendCurrency || "").toUpperCase();
  const receiveCode = (receiveCurrency || "").toUpperCase();
  const feeCode = (feeCurrencyCode || receiveCurrency || "").toUpperCase();
  const spendDisplay = formatConvertResultAmount(spendAmount, spendCurrency);
  const feeDisplay = totalFee ? `${formatConvertResultAmount(totalFee, feeCode)} ${feeCode}` : "—";
  const rateLine = formatConvertResultRateLine(exchangeRate, spendCode, receiveCode, orderType);

  const rows = [
    {
      key: "type",
      title: t("convert.type"),
      value: t(typeValueKey),
    },
    {
      key: "payFrom",
      title: t("convert.payFrom"),
      value: spendDisplay ? `${spendDisplay} ${spendCode}` : "—",
      subValue: rateLine || undefined,
    },
    {
      key: "to",
      title: t("convert.to"),
      value: receiveCode || "—",
    },
    {
      key: "fee",
      title: t("convert.transactionFees"),
      value: feeDisplay,
    },
  ];

  if (includeTradeDate) {
    rows.push({
      key: "tradeDate",
      title: t("convert.tradeDate"),
      value: formatConvertTradeDate(tradeDate),
    });
  }

  return rows;
}
